import {urlBuilderFactory} from "./url-builder-factory.ts";

interface GetUrlPathFn {
    /**
     * Get a URL path to the given path, with associated continue path.
     *
     * This uses our underlying URL builder factory and the URL rules set with
     * `configureUrlConstructionRules` to build the correct URL, including any
     * relevant params. This is equivalent to calling
     * `urlBuilderFactory(path)(continuePath)`.
     *
     * @param pathId The path ID for the URL.
     * @param continuePath The continue path to append to the URL.
     * @returns The URL path.
     */
    (pathId: string, continuePath?: Nullable<string>): string;

    /**
     * Get a URL path to the given path, with associated params.
     *
     * This uses our underlying URL builder factory and the URL rules set with
     * `configureUrlConstructionRules` to build the correct URL, including any
     * relevant params. This is equivalent to calling
     * `urlBuilderFactory(path)(params)`.
     *
     * @param pathId The path ID for the URL.
     * @param params The params to append to the URL.
     * @returns The URL path.
     */
    (pathId: string, params: Record<string, string>): string;

    /**
     * Get a URL path to the given path, with associated params.
     *
     * This uses our underlying URL builder factory and the URL rules set with
     * `configureUrlConstructionRules` to build the correct URL, including any
     * relevant params. This is equivalent to calling
     * `urlBuilderFactory(path)(params)`.
     *
     * @param pathId The path ID for the URL.
     * @param params The params to append to the URL.
     * @returns The URL path.
     */
    (pathId: string, params: URLSearchParams): string;
}

/**
 * Get a URL path to the given path, with associated continue or params.
 *
 * This uses our underlying URL builder factory and the URL rules set with
 * `configureUrlConstructionRules` to build the correct URL, including any
 * relevant params. This is equivalent to calling
 * `urlBuilderFactory(path)(params)`.
 */
export const getUrlPath: GetUrlPathFn = (
    pathId: string,
    continuePathOrParams?:
        | Nullable<string>
        | Record<string, string>
        | URLSearchParams,
) => urlBuilderFactory(pathId)(continuePathOrParams as any);
